import { cn } from '@/lib/utils';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Muscle } from '@chiroup/core/types/Muscle.type';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';

type Props = {
  muscle?: Muscle;
  value: {
    tender?: boolean;
    tp?: boolean;
    hpn?: boolean;
    spasm?: boolean;
    weakness?: boolean;
    left?: boolean;
    right?: boolean;
  };
  onChange: (value: {
    muscle: number;
    status?: string;
    e?: boolean;
    left?: boolean;
    right?: boolean;
  }) => void;
  remove: () => void;
  firstItem?: boolean;
  showRingAroundInput?: boolean;
  disabled?: boolean;
};

const MyofascialItem: React.FC<Props> = ({
  muscle,
  onChange,
  value,
  remove,
  firstItem,
  showRingAroundInput,
  disabled,
}) => {
  const handleSideChange = (side: string) => {
    if (disabled) return;
    if (side === 'left') {
      onChange({
        muscle: muscle?.ID as number,
        left: !value.left,
        right: false,
      });
    } else {
      onChange({
        muscle: muscle?.ID as number,
        left: false,
        right: !value.right,
      });
    }
  };

  return (
    <li
      className={cn(
        'block bg-white hover:bg-gray-50 dark:bg-dark-gray-800 overflow-hidden dark:hover:bg-dark-gray-700',
        firstItem ? 'rounded-t-lg' : ''
      )}
    >
      <div className="flex flex-col gap-2 px-4 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900 truncate dark:text-dark-gray-50">
            {muscle?.name}
          </p>
          {!disabled && (
            <div className="flex items-center justify-center">
              <Icon
                icon={Icons.close}
                className="w-4 h-4 text-gray-300 hover:text-gray-900"
                onClick={remove}
              />
            </div>
          )}
        </div>
        <div className="mt-2 sm:flex sm:justify-between">
          <div className="flex shrink-0 pt-2 sm:flex-row flex-wrap">
            <Checkbox
              label="Tender"
              name={`${muscle?.ID}-tender`}
              value={value.tender || false}
              showRingAroundInput={showRingAroundInput}
              disabled={disabled}
              onChange={e =>
                onChange({
                  muscle: muscle?.ID as number,
                  status: 'tender',
                  e,
                  left: value.left,
                  right: value.right,
                })
              }
            />
            <Checkbox
              label="TP"
              name={`${muscle?.ID}-tp`}
              showRingAroundInput={showRingAroundInput}
              disabled={disabled}
              onChange={e =>
                onChange({
                  muscle: muscle?.ID as number,
                  status: 'tp',
                  e,
                  left: value.left,
                  right: value.right,
                })
              }
              value={value.tp || false}
            />
            <Checkbox
              label="HPN"
              name={`${muscle?.ID}-hpn`}
              showRingAroundInput={showRingAroundInput}
              disabled={disabled}
              onChange={e =>
                onChange({
                  muscle: muscle?.ID as number,
                  status: 'hpn',
                  e,
                  left: value.left,
                  right: value.right,
                })
              }
              value={value.hpn || false}
            />
            <Checkbox
              label="Spasm"
              name={`${muscle?.ID}-spasm`}
              disabled={disabled}
              onChange={e =>
                onChange({
                  muscle: muscle?.ID as number,
                  status: 'spasm',
                  e,
                  left: value.left,
                  right: value.right,
                })
              }
              value={value.spasm || false}
            />
            <Checkbox
              label="Weakness"
              name={`${muscle?.ID}-weakness`}
              disabled={disabled}
              onChange={e =>
                onChange({
                  muscle: muscle?.ID as number,
                  status: 'weakness',
                  e,
                  left: value.left,
                  right: value.right,
                })
              }
              value={value.weakness || false}
            />
          </div>
          <div className="p-2 sm:p-0">
            <button
              type="button"
              disabled={disabled}
              className={[
                'm-2 sm:m-0 relative inline-flex items-center rounded sm:rounded-l-md border sm:rounded-r-none  sm:border-l sm:border-t sm:border-b px-4 py-1 text-sm font-medium transition duration-150 ease-in-out w-full sm:w-auto justify-center border-r dark:text-dark-gray-100',
                value.left
                  ? 'bg-primary-600 border-primary-600 text-white  outline-none border-r-transparent '
                  : value.right
                    ? 'border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 text-gray-700 border-r-transparent '
                    : 'border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 text-gray-700',
                disabled ? 'opacity-50 cursor-not-allowed' : '',
              ].join(' ')}
              onClick={() => {
                handleSideChange('left');
              }}
            >
              L
            </button>
            <button
              type="button"
              disabled={disabled}
              className={[
                'm-2 sm:m-0 relative rounded inline-flex items-center sm:rounded-r-md sm:rounded-l-none border sm:border-r sm:border-t sm:border-b px-4 py-1 text-sm font-medium transition duration-150 ease-in-out w-full sm:w-auto justify-center dark:text-dark-gray-100',
                value.right
                  ? 'bg-primary-600 border-primary-600 text-white  outline-none '
                  : 'border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 text-gray-700 ',
                disabled ? 'opacity-50 cursor-not-allowed' : '',
              ].join(' ')}
              onClick={() => {
                handleSideChange('right');
              }}
            >
              R
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default MyofascialItem;
